<script>
  import { select, selectAll } from "d3-selection"
  import { extent } from "d3-array"
  import { scaleLinear, scaleBand } from "d3-scale";
  import tippy from "sveltejs-tippy";
  import Legend from "./Legend.svelte"
  import { onMount } from 'svelte';

  export let allData;
  export let selectedCountries;
  export let isMobile;
  

  // const { countries, ports } = allData

  $: ports = allData.ports
  $: countries = allData.countries

  let svg
	let width = 1200
	let height = 600

  const mobileBreakpoint = 768

	const margin = { top: 5, right: 10, bottom: 10, left: 10 }

	$: xScale = scaleLinear()
		.domain(extent(ports, (p) => p.rank).reverse())
		.range([margin.left, width - margin.right])

  $: xRange = xScale.range()

  $: groups = [...new Set(ports.map(p => p.region_world_bank))].sort((a, b) => a.localeCompare(b))

  // Add a buffer band to make space for axis labels
  $: yTicks = ['start', ...groups]

	$: yScale = scaleBand()
		.domain(yTicks)
		.range([margin.bottom, height - margin.top])
    .paddingOuter(0.5)
    .paddingInner(1)

  $: yBandwidth = yScale.step()

  $: zScale = scaleLinear()
		.domain(extent(ports, (p) => p.value))
		.range([5, yBandwidth])
    .nice()

  function portsByGroup(group) {
    return ports.filter((p) => p.region_world_bank === group)
  }

  function getItemClasses(port, selectedCountries) {
    let itemClass = ''

    if (selectedCountries.includes(port.iso)) {
      const country = countries.get(port.iso)
      const mostPort = country.most_port.port
      const leastPort = country.least_port.port

      if (port.port == mostPort || port.port == leastPort) {
        itemClass += 'port--extent'
      }

      if (port.iso == selectedCountries[0]) {
        itemClass += ' color--primary'
      } else if (port.iso == selectedCountries[1]) {
        itemClass += ' color--secondary'
      }
    }
    
    return itemClass
  }

  const formatTooltip = (data, selector) => {
    const template = `
      <h2 class="tooltip__heading">${data.port},<wbr> ${data.country}</h2>
      <ul class="port-values" role="list">
        <li><div>Rank:</div> ${data.rank}</li>
        <li><div>Score:</div> ${data.value_formatted}</li>
      </ul>
    `;

    return {
      content: template,
      allowHTML: true,
      placement: "top",
      maxWidth: 175,
      triggerTarget: document.getElementById(selector),
      touch: false,
    };
  };

</script>

<figure class="interactive__charts" bind:clientWidth={width} bind:clientHeight={height}>
  <svg>
    <!-- x axis -->
    <g class='axis axis--x' transform="translate(0, {margin.top})">
      <g class='tick' transform='translate({xRange[0]},0)'>
        <text dy="0.71em" text-anchor="start">&#8592; Less Connected</text>
      </g>
      <g class='tick' transform='translate({xRange[1]},0)'>
        <text dy="0.71em" text-anchor="end">More Connected &#8594;</text>
      </g>
    </g>

    <!-- y axis -->
    <g class='axis axis--y' transform="translate({margin.left}, 0)">
      {#each groups as group}
        <g class="tick" transform="translate(0,{yScale(group)})">
        <line stroke="currentColor" y1="0" y2="0" x1="0" x2="{width - margin.right - margin.left}" />
        <text class="interactive__subheading" dy="0.32em" y="11" text-anchor="start" fill="currentColor">
          {group}
        </text>
      </g>
      {/each}
    </g>

    <!-- data -->
    {#each groups as group, groupIndex}
      <g class="chart__region" transform="translate(0, {yScale(group)})">
        {#each portsByGroup(group) as port, portIndex}
          {#if isMobile}
            <g 
              class="{`chart__item ${getItemClasses(port, selectedCountries)}`}"
              transform="translate({xScale(port.rank)}, 0)"
            >
            <line 
              class="chart__line"
              x1="0"
              x2="0"
              y1="0"
              y2="{-zScale(port.value)}"
            />
            <text class="chart__rank" x="0" y="{-zScale(port.value) - 2}">{port.rank}</text>
          </g>
          {:else}
          <g 
            class="{`chart__item ${getItemClasses(port, selectedCountries)}`}"
            transform="translate({xScale(port.rank)}, 0)"
            use:tippy={formatTooltip(
              port,
              `tooltip-node-${groupIndex}-${portIndex}`
            )}
          >
            <line 
              class="chart__line"
              x1="0"
              x2="0"
              y1="0"
              y2="{-zScale(port.value)}"
            />
            <text class="chart__rank" x="0" y="{-zScale(port.value) - 2}">{port.rank}</text>
          </g>
          {/if}
        {/each}
      </g>
    {/each}
  </svg>
  <Legend />
</figure>

<style type="text/scss">
  @import "../scss/custom/_chart.scss";
</style>
